import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sprout, MapPin, ShoppingBasket, Store, Search, Truck,
  Leaf, PackageCheck, ClipboardList, ArrowRight, BadgeCheck,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const buyerSteps = [
  { icon: Search, title: "Search your postcode", text: "Search your postcode and see producers near you." },
  { icon: ShoppingBasket, title: "Pre-order online", text: "Pre-order what you want, from as many producers as you like." },
  { icon: Truck, title: "Collect or get it delivered", text: "Collect or take delivery — and pay them direct." },
];

const sellerSteps = [
  { icon: MapPin, title: "Add your details", text: "Add your details and location — you'll appear on the map." },
  { icon: ClipboardList, title: "List what you sell", text: "List what you sell, with photos, prices and collection days." },
  { icon: PackageCheck, title: "Take pre-orders", text: "Take pre-orders from local people and get paid direct." },
];

const PRODUCER_TYPES_STRIP = [
  "Farms", "Smallholdings", "Butchers", "Bakers", "Beekeepers", "Cheesemakers", "Drinks", "Preserves",
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="pt-16 min-h-screen bg-grain overflow-hidden">
      {/* Hero */}
      <section className="relative">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 560px at 18% 0%, rgba(143,181,115,0.28), transparent 60%), radial-gradient(900px 520px at 85% 10%, rgba(231,220,200,0.5), transparent 55%)",
          }}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-10 sm:pt-20 sm:pb-16">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div>
              <motion.div initial="hidden" animate="show" variants={fadeUp} className="mb-5">
                <img src="/logo-wordmark.png" alt="Farm Gate — Farm Fresh. Local Discovery." className="h-16 sm:h-20 w-auto" />
              </motion.div>

              <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-primary leading-[1.05]">
                Real food from local producers near you
              </motion.h1>

              <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp}
                className="mt-5 text-lg text-foreground/80 max-w-xl">
                Farms, smallholdings, butchers, bakers, beekeepers and cheesemakers — all selling direct. Pre-order online and simply
                <span className="font-medium text-foreground"> pay on collection or delivery</span>. No middlemen, just honest local food.
              </motion.p>

              <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp}
                className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="h-12 text-base" onClick={() => navigate("/map")} data-testid="landing-shop-button">
                  <ShoppingBasket className="h-5 w-5 mr-2" /> Shop Local Producers
                </Button>
                <Button size="lg" variant="secondary"
                  className="h-12 text-base bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white border-0"
                  onClick={() => navigate("/sell/register")} data-testid="landing-sell-button">
                  <Store className="h-5 w-5 mr-2" /> Sell Your Food
                </Button>
              </motion.div>

              {/* Producer types strip */}
              <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp}
                className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-foreground/70"
                data-testid="landing-producer-types-strip">
                {PRODUCER_TYPES_STRIP.map((t, i) => (
                  <React.Fragment key={t}>
                    <span className="font-medium">{t}</span>
                    {i < PRODUCER_TYPES_STRIP.length - 1 && <span className="text-muted-foreground/50" aria-hidden="true">·</span>}
                  </React.Fragment>
                ))}
              </motion.div>

              <motion.div initial="hidden" animate="show" custom={5} variants={fadeUp}
                className="mt-6 flex flex-wrap gap-2">
                <Badge className="bg-[#EEF4E6] text-[#3E5A1F] border"><Leaf className="h-3 w-3 mr-1" />Know where it came from</Badge>
                <Badge className="bg-[#E6F2E8] text-[#1F5A2F] border"><BadgeCheck className="h-3 w-3 mr-1" />Certified & uncertified welcome</Badge>
                <Badge className="bg-[#F3EEE6] text-[#5A3A1F] border"><Truck className="h-3 w-3 mr-1" />Collection & delivery</Badge>
              </motion.div>

              <motion.p initial="hidden" animate="show" custom={6} variants={fadeUp}
                className="mt-4 text-sm text-muted-foreground max-w-xl flex items-start gap-1.5">
                <Store className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--fg-wood-600)]" />
                <span>No shop needed. No certification needed. Whether you've got two hundred acres or twelve beehives, if you sell food direct, you belong here.</span>
              </motion.p>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }} className="relative">
              <div className="rounded-3xl overflow-hidden shadow-lift border border-border">
                <img
                  src="https://images.pexels.com/photos/9465508/pexels-photo-9465508.jpeg?auto=compress&cs=tinysrgb&w=940"
                  alt="UK farmland"
                  className="w-full h-[300px] sm:h-[420px] object-cover"
                />
              </div>
              <Card className="absolute -bottom-5 -left-3 sm:left-6 rounded-2xl shadow-lg p-4 w-56">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-primary text-primary-foreground grid place-items-center">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-semibold leading-none">Local producers</div>
                    <div className="text-xs text-muted-foreground mt-1">Right on your doorstep</div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-card border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-semibold text-primary">How it works</h2>
            <p className="text-muted-foreground mt-2">Simple for shoppers, simple for producers.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Buyers */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <span className="h-9 w-9 rounded-xl bg-secondary grid place-items-center text-primary"><ShoppingBasket className="h-5 w-5" /></span>
                <h3 className="text-xl font-semibold">For shoppers</h3>
              </div>
              <div className="space-y-4">
                {buyerSteps.map((s, i) => (
                  <StepRow key={s.title} index={i + 1} {...s} />
                ))}
              </div>
              <Button className="mt-6" onClick={() => navigate("/map")} data-testid="landing-howitworks-shop">
                Start shopping <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Sellers */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <span className="h-9 w-9 rounded-xl bg-[#F3EEE6] grid place-items-center text-[color:var(--fg-wood-600)]"><Store className="h-5 w-5" /></span>
                <h3 className="text-xl font-semibold">For producers</h3>
              </div>
              <div className="space-y-4">
                {sellerSteps.map((s, i) => (
                  <StepRow key={s.title} index={i + 1} accent {...s} />
                ))}
              </div>
              <Button className="mt-6 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white"
                onClick={() => navigate("/sell/register")} data-testid="landing-howitworks-sell">
                List your business <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold">Ready to taste the difference?</h2>
          <p className="opacity-85 mt-2">Join Farm Gate and support local producers near you.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="secondary" className="h-12" onClick={() => navigate("/map")}>
              <MapPin className="h-5 w-5 mr-2" /> Explore the map
            </Button>
            <Button size="lg" className="h-12 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white"
              onClick={() => navigate("/auth")}>
              Sign in / Register
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

const StepRow = ({ index, icon: Icon, title, text, accent }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.05 }}>
    <Card className="rounded-2xl p-4 flex gap-4 items-start hover:shadow-md transition-shadow">
      <div className="relative shrink-0">
        <span className={`h-11 w-11 rounded-xl grid place-items-center ${accent ? "bg-[#F3EEE6] text-[color:var(--fg-wood-600)]" : "bg-secondary text-primary"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <span className="absolute -top-2 -left-2 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[11px] grid place-items-center font-semibold">{index}</span>
      </div>
      <div>
        <h4 className="font-semibold leading-tight">{title}</h4>
        <p className="text-sm text-muted-foreground mt-1">{text}</p>
      </div>
    </Card>
  </motion.div>
);
